import type { Community } from '@communities/community/domain/Community';
import { AuditSessionsFilters } from '@internal-audit/components/AuditSessionsFilters/AuditSessionsFilters';
import type { AuditSessionFilters, AuditSessionSort, FilterOptions, AuditSessionPageSize } from '@internal-audit/components/AuditSessionsFilters/types';
import { AuditSessionsPageHeader } from '@internal-audit/components/AuditSessionsPageHeader';
import { AuditSessionsTable } from '@internal-audit/components/AuditSessionsTable';
import { CreateAuditSessionDialog } from '@internal-audit/components/CreateAuditSessionDialog';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';

import { Pagination } from '../../../core/components';
import * as styles from '../AuditSessionsListPage.css';

interface SessionsPageContentProps {
  community: Community | null;
  filteredAndSortedSessions: FlexibleAuditSession[];
  filters: AuditSessionFilters;
  sort: AuditSessionSort;
  filterOptions: FilterOptions;
  sessionsLoading: boolean;
  filterOptionsLoading: boolean;
  isCreateDialogOpen: boolean;
  communityId: string;
  // Pagination props
  currentPage: number;
  totalPages: number;
  pageSize: AuditSessionPageSize;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AuditSessionPageSize) => void;
  // Event handlers
  onFiltersChange: (filters: AuditSessionFilters) => void;
  onSortChange: (sort: AuditSessionSort) => void;
  onViewSession: (session: FlexibleAuditSession) => void;
  onDeleteSuccess: () => void;
  onCloseCreateDialog: () => void;
  onSessionCreated: () => void;
  onCreateSession: () => void;
}

/**
 * Main content section component with page header, filters, table, and dialog
 * Contains audit sessions page header, filters, table, and create session dialog
 */
export function SessionsPageContent({
  community,
  filteredAndSortedSessions,
  filters,
  sort,
  filterOptions,
  sessionsLoading,
  filterOptionsLoading,
  isCreateDialogOpen,
  communityId,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
  onSortChange,
  onViewSession,
  onDeleteSuccess,
  onCloseCreateDialog,
  onSessionCreated,
  onCreateSession,
}: SessionsPageContentProps) {
  return (
    <div className={styles.contentSection}>
      <div className={styles.contentContainer}>
        {/* Page Header - moved from hero section to content section */}
        <AuditSessionsPageHeader
          community={community}
          isLoading={sessionsLoading || filterOptionsLoading}
          sessionCount={filteredAndSortedSessions.length}
          onCreateSession={onCreateSession}
        />

        {/* New Redesigned Filters Component */}
        <AuditSessionsFilters
          filterOptions={filterOptions}
          filters={filters}
          isLoading={sessionsLoading}
          pageSize={pageSize}
          sort={sort}
          onFiltersChange={onFiltersChange}
          onPageSizeChange={onPageSizeChange}
          onSortChange={onSortChange}
        />

        <AuditSessionsTable isLoading={sessionsLoading} sessions={filteredAndSortedSessions} onDeleteSession={onDeleteSuccess} onViewSession={onViewSession} />

        {/* Pagination Controls */}
        <Pagination currentPage={currentPage} disabled={sessionsLoading} totalPages={totalPages} onPageChange={onPageChange} />

        {/* Create Session Dialog */}
        <CreateAuditSessionDialog
          communityId={communityId}
          communityName={community?.name || 'Unknown Community'}
          isOpen={isCreateDialogOpen}
          onClose={onCloseCreateDialog}
          onSessionCreated={onSessionCreated}
        />
      </div>
    </div>
  );
}
